'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import Link from 'next/link';
import './people.css';

/* Saari styling people.css mein hai.

   IMAGE PATHS:
   - Hero + Final CTA background -> people.css ke :root mein (--people-hero-bg, --people-cta-bg)
   - Blend cards + People stories -> neeche `blend` aur `stories` arrays (image field)

   Sab images public/assets/images/people/ mein rakho.

   Links: "Join Our Team" (hero + final CTA) -> /careers (header ke Careers page par).
   "View More People Stories" ka abhi koi page nahi hai (TODO niche). */

type IconName =
  | 'users' | 'user' | 'briefcase' | 'building' | 'gear' | 'database' | 'brain'
  | 'globe' | 'sprout' | 'bulb' | 'target' | 'chart' | 'hands';

const ICON_PATHS: Record<IconName, ReactNode> = {
  users: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.3" /><path d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6M15.5 14.3c3 .2 5.5 2 5.5 5.2" /></>,
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5 20c0-4 3-6.5 7-6.5s7 2.5 7 6.5" /></>,
  briefcase: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 13h18" /></>,
  building: <><path d="M5 21V4h9v17M14 9h5v12M3 21h18M8 8h3M8 12h3M8 16h3" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
  database: <><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>,
  brain: <><path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-2 5 3 3 0 0 0 2 5 3 3 0 0 0 5 1V4a2.5 2.5 0 0 0-2 0zM15 4a3 3 0 0 1 3 3 3 3 0 0 1 2 5 3 3 0 0 1-2 5 3 3 0 0 1-5 1V4" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" /></>,
  sprout: <><path d="M12 21V11M12 11C12 7 9 5 4 5c0 4 2 6 8 6zM12 13c0-3 2-5 7-5 0 3-2 5-7 5z" /></>,
  bulb: <><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /></>,
  target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" /></>,
  chart: <><path d="M5 20v-7M11 20V8M17 20V4M3 20h18" /></>,
  hands: <><circle cx="12" cy="7" r="2.5" /><circle cx="5.5" cy="10" r="2" /><circle cx="18.5" cy="10" r="2" /><path d="M8 20c0-3 1.8-5 4-5s4 2 4 5M2 19c0-2.3 1.5-4 3.5-4M22 19c0-2.3-1.5-4-3.5-4" /></>,
};

function Icon({ name }: { name: IconName }) {
  return (
    <svg className="people-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[name]}
    </svg>
  );
}

function Arrow() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
    </svg>
  );
}

/* ============================== DATA ============================== */

const heroWords = ['BUSINESS', 'INDUSTRY', 'TECHNOLOGY', 'DATA & AI', 'DELIVERY'];

const stats: { icon: IconName; value?: string; title: string; text: string }[] = [
  { icon: 'users', value: '230+', title: 'Professionals', text: 'Across the Orane family' },
  { icon: 'user', title: 'Balanced Talent Mix', text: 'Experienced + Emerging' },
  { icon: 'globe', title: 'Pan-India and Global', text: 'Delivery Capability' },
  { icon: 'brain', title: 'Continuous Learning', text: 'Certifications & Skills' },
  { icon: 'hands', title: 'Inclusive Culture', text: 'Equal Opportunities' },
  { icon: 'chart', title: 'Growth Together', text: 'People, Customers, Society' },
];

const blend: { icon: IconName; title: string; text: string; image: string }[] = [
  { icon: 'briefcase', title: 'Business Understanding', text: 'We start with the business question, not just the technology.', image: '/assets/images/ourpeople3.jpg' },
  { icon: 'building', title: 'Industry Context', text: 'Our people bring deep experience across industries and functions.', image: '/assets/images/ourpeople4.jpg' },
  { icon: 'gear', title: 'Technology Capability', text: 'From enterprise systems to emerging technologies, we build, integrate and simplify.', image: '/assets/images/ourpeople5.jpg' },
  { icon: 'database', title: 'Data and AI', text: 'We use data and AI to create insights, automate processes and enable better decisions.', image: '/assets/images/ourpeople6.jpg' },
  { icon: 'users', title: 'Delivery Experience', text: 'Our people have done the work, from blueprint to go-live to ongoing support.', image: '/assets/images/ourpeople8.jpg' },
];

const culture: { icon: IconName; title: string; text: string }[] = [
  { icon: 'users', title: 'One Team', text: 'Collaborate across geographies, industries and capabilities.' },
  { icon: 'sprout', title: 'Continuous Learning', text: 'Certifications, training and real project experience.' },
  { icon: 'bulb', title: 'Ideas to Impact', text: 'Encourage curiosity, innovation and experimentation.' },
  { icon: 'hands', title: 'Inclusive Workplace', text: 'Diverse people, perspectives and opportunities.' },
  { icon: 'target', title: 'Growth Together', text: 'When our people grow, our customers and communities grow too.' },
];

const stories = [
  { quote: 'I started as an ECAP trainee and today I work on global projects.', name: 'Pragati', role: 'SAP HCM Consultant', image: '/assets/images/profile-pic.jpg' },
  { quote: 'What I value most is the opportunity to work on real business problems.', name: 'Sanjay', role: 'SAP SD Consultant', image: '/assets/images/profile-pic-2.jpg' },
  { quote: 'At Orane, I am learning every day — from people who have done it.', name: 'Aarushi', role: 'PMO Analyst', image: '/assets/images/profile-pic.jpg' },
  { quote: 'The exposure to different industries and clients has accelerated my growth.', name: 'Umar', role: 'Consultant – Africa Delivery', image: '/assets/images/Umar.jpg' },
];

const ctaPoints = ['More Knowledge', 'More Capability', 'More Opportunity', 'More Value', 'Together'];

/* ===================== SCROLL ANIMATION ===================== */
/* Sirf fade-up (neeche se upar) aur zoom. Left/right slide nahi. */
const animGroups: [string, 'fade-up' | 'zoom'][] = [
  ['.sec-head', 'fade-up'],
  ['.stat-card', 'zoom'],
  ['.blend-card', 'fade-up'],
  ['.eco-card', 'zoom'],
  ['.story-card', 'fade-up'],
  ['.final-cta-box', 'zoom'],
  ['.final-cta-chain', 'fade-up'],
];

function useScrollReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    animGroups.forEach(([selector, type]) => {
      const perParent = new Map<Element, number>();
      el.querySelectorAll<HTMLElement>(selector).forEach((node) => {
        const parent = node.parentElement as Element;
        const i = perParent.get(parent) ?? 0;
        perParent.set(parent, i + 1);

        node.classList.add('rv', `rv--${type}`);
        node.style.setProperty('--rv-delay', `${(i % 6) * 80}ms`); // stagger
        io.observe(node);
      });
    });

    return () => io.disconnect();
  }, [root]);
}

/* ============================== PAGE ============================== */

export default function OurPeople() {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="people-page" ref={pageRef}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="people-hero" data-purpose="hero-section">
          <div className="people-hero-bg-image"></div>
          <div className="people-hero-bg-overlay"></div>
          <div className="people-hero-grid"></div>
          <div className="people-hero-glow people-hero-glow--left"></div>
          <div className="people-hero-glow people-hero-glow--right"></div>

          <div className="op-container op-container--left people-hero-inner">
            <div className="people-hero-content">
              <h1 className="people-hero-title">Our People.</h1>
              <p className="people-hero-subtitle">People Who Understand More Than Technology.</p>
              <p className="people-hero-text">
                Our people bring together business understanding, industry context, technology
                capability, data and AI and a commitment to real outcomes.
              </p>

              <div className="people-hero-actions">
                <Link className="btn-primary" href="/careers">
                  Join Our Team
                  <Arrow />
                </Link>
                <Link className="btn-glass" href="#culture">
                  Explore Our Culture
                  <span className="play-icon">▶</span>
                </Link>
              </div>

              <div className="people-hero-words">
                {heroWords.map((w) => (
                  <span className="people-hero-word" key={w}>{w}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============================= STATS ============================= */}
        <section className="op-section op-section--white" data-purpose="people-stats">
          <div className="op-container">
            <div className="stat-grid">
              {stats.map((s) => (
                <div className="stat-card" key={s.title}>
                  <div className="stat-icon">
                    <Icon name={s.icon} />
                  </div>
                  {s.value && <span className="stat-value">{s.value}</span>}
                  <span className="stat-title">{s.title}</span>
                  <span className="stat-text">{s.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================= BLEND ============================= */}
        <section className="op-section op-section--white" data-purpose="people-blend">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">What We Bring</div>
              <h2 className="sec-title">Our People Bring a Unique Blend</h2>
              <p className="sec-sub">
                Business understanding, industry expertise, technology capability, data and AI, and
                real-world delivery experience.
              </p>
            </div>

            <div className="blend-grid">
              {blend.map((b, i) => (
                <article className="blend-card" key={b.title}>
                  <div className="blend-img">
                    <img src={b.image} alt="" loading="lazy" />
                  </div>
                  <span className="blend-icon">
                    <Icon name={b.icon} />
                  </span>
                  <div className="blend-body">
                    <small>{String(i + 1).padStart(2, '0')}</small>
                    <h3>{b.title}</h3>
                    <p>{b.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ CULTURE ============================ */}
        <section className="op-section op-section--white" data-purpose="people-culture" id="culture">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">Our Culture</div>
              <h2 className="sec-title">Built on Trust, Learning and Shared Success</h2>
              <p className="sec-sub sec-sub--base">
                A collaborative, inclusive and high-performance culture.
              </p>
            </div>

            <div className="culture-grid">
              {culture.map((c) => (
                <div className="eco-card" key={c.title}>
                  <div className="eco-icon">
                    <Icon name={c.icon} />
                  </div>
                  <h3 className="eco-title">{c.title}</h3>
                  <p className="eco-text">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ STORIES ============================ */}
        <section className="op-section op-section--white" data-purpose="people-stories">
          <div className="op-container">
            <div className="opview-head">
              <div>
                <div className="sec-badge">Real Journeys</div>
                <h2 className="sec-title">People Stories</h2>
                <p className="sec-sub sec-sub--base">Real journeys. Real growth. Real impact.</p>
              </div>
              {/* TODO: People Stories ka page aane par yahan href badlo (abhi koi page nahi hai) */}
              <Link className="btn-small" href="#">
                View More People Stories
                <Arrow />
              </Link>
            </div>

            <div className="story-grid">
              {stories.map((s) => (
                <figure className="story-card" key={s.name}>
                  <img src={s.image} alt={`Portrait of ${s.name}`} loading="lazy" />
                  <figcaption>
                    <blockquote>“{s.quote}”</blockquote>
                    <b>{s.name}</b>
                    <span>{s.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="final-cta" data-purpose="final-call-to-action" id="careers">
          <div className="final-cta-bg"></div>
          <div className="final-cta-overlay"></div>
          <div className="op-container final-cta-inner">
            <div className="final-cta-box">
              <div className="final-cta-left">
                <span className="final-cta-eyebrow">DIFFERENT PEOPLE. A COMMON PURPOSE.</span>
                <h2 className="final-cta-title">Do More. Together.</h2>
                <p className="final-cta-text">
                  Different expertise, one purpose: real progress for our customers, our people and
                  the markets we serve.
                </p>
              </div>
              <div className="final-cta-right">
                <Link className="btn-primary btn-primary--cta" href="/careers">
                  Join Our Team
                  <Arrow />
                </Link>
              </div>
            </div>

            <div className="final-cta-values">
              {ctaPoints.map((v, i) => (
                <span className="final-cta-chain" key={v}>
                  <span className="final-cta-value">{v.toUpperCase()}</span>
                  {i < ctaPoints.length - 1 && <span className="final-cta-arrow">→</span>}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}